import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { graphql } from 'gatsby';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';

import { containerStyles } from '../../styles/layout';
import { rem } from '../../utilities/style';
import { body, headers } from '../../styles/type';
import { colors } from '../../theme/colors';
import TextRenderer from '../TextRenderer';

import { ReactComponent as SplashLeft } from '../../images/icons/photo-header-accent.svg';
import { media } from '../../theme/media';

const Container = styled.div`
  position: relative;
  overflow: hidden;
  padding: 0 0 ${rem(25)};
  background-color: ${colors.white};

  ${({ backgroundPattern }) => backgroundPattern === 'Dark Gradient' && `
    background-color: ${colors.blue['800']};
  `}

  ${media('>small')} {
    padding: ${rem(100)} 0;

    ${({ backgroundPattern }) => backgroundPattern === 'Dark Gradient' && `
      &::after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-image: linear-gradient(90deg, rgba(0,28,51,1) 32%, rgba(0,28,51,0) 100%);
      }
    `}
  }
`;

const BackgroundImage = styled(GatsbyImage)`
  position: absolute;
  object-fit: cover;
  object-position: right center;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: none;

  ${media('>small')} {
    display: block;
  }
`;

const StyledSplashLeft = styled(SplashLeft)`
  position: absolute;
  z-index: 1;
  top: 0;
  left: 0;
  width: auto;
  height: 100%;
  display: none;

  ${media('>small')} {
    display: block;
  }
`;

const ContentWrapper = styled.div`
  ${containerStyles.regular}
  position: relative;
  z-index: 1;
`;

const MobileImage = styled(GatsbyImage)`
  width: 100%;
  margin-bottom: ${rem(25)};
  ${media('>small')} {
    display: none;
  }
`;

const Headline = styled.h1`
  ${headers.h2}
  margin-bottom: ${rem(20)};
  max-width: ${rem(640)};
  color: ${colors.blue['800']};

  ${({ backgroundPattern }) => backgroundPattern === 'Dark Gradient' && `
    color: ${colors.white};
  `}
`;

const Subhead = styled.h2`
  ${body.large}
  max-width: ${rem(540)};
  color: ${colors.gray['600']};

  ${({ backgroundPattern }) => backgroundPattern === 'Dark Gradient' && `
    color: ${colors.white};
  `}
`;

const PhotoHeader = ({ useH1, headline, subhead, image, mobileImage, backgroundPattern }) => (
  <Container backgroundPattern={backgroundPattern}>
    {image && (
      <BackgroundImage
        image={getImage(image)}
        alt={image?.description}
        loading="eager"
      />
    )}
    {backgroundPattern === 'Dark Gradient' && (
      <StyledSplashLeft />
    )}
    {mobileImage && (
      <MobileImage
        image={getImage(mobileImage)}
        alt={image?.description}
        loading="eager"
      />
    )}
    <ContentWrapper>
      {headline && (
        <Headline as={useH1 ? 'h1' : 'h2'} backgroundPattern={backgroundPattern}>
          <TextRenderer text={headline} />
        </Headline>
      )}
      {subhead && (
        <Subhead backgroundPattern={backgroundPattern}>
          <TextRenderer text={subhead} />
        </Subhead>
      )}
    </ContentWrapper>
  </Container>
);

PhotoHeader.propTypes = {
  headline: PropTypes.string,
  subhead: PropTypes.string,
  image: PropTypes.shape({
    // eslint-disable-next-line react/forbid-prop-types
    gatsbyImageData: PropTypes.object
  }),
  mobileImage: PropTypes.shape({
    // eslint-disable-next-line react/forbid-prop-types
    gatsbyImageData: PropTypes.object
  }),
  backgroundPattern: PropTypes.oneOf(['Dark Gradient', 'No Gradient'])
};

export const query = graphql`
  fragment PhotoHeaderFragment on ContentfulPhotoHeader {
    id
    __typename
    headline
    subhead
    image {
      gatsbyImageData(width: 1440, height: 400, quality: 75, resizingBehavior: FILL)
      description
    }
    mobileImage {
      gatsbyImageData(width: 720, height: 430, quality: 75, resizingBehavior: FILL)
      description
    }
    backgroundPattern
      personalizations {
        ...PersonalizationFragment
    }
  }
`;

export default PhotoHeader;
